import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Card,
  Typography,
  Chip,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  Grid,
  FormControlLabel,
  Checkbox,
  Stack,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Fab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Skeleton,
  Alert,
  Divider,
  Tooltip,
} from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import {
  Search,
  Person,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Diversity3 as DiversityIcon,
  SwapHoriz as TransferIcon,
  Visibility as VisibilityIcon,
  EmojiEvents as TrophyIcon,
  CalendarToday as CalendarIcon,
  School as SchoolIcon,
  PersonAdd as PersonAddIcon,
  Groups as GroupsIcon,
  EventRepeat as EventRepeatIcon,
  SportsSoccer as SportsIcon,
} from "@mui/icons-material";

import apiFetch from "services/http";
import { useSeason } from "context/SeasonContext";
import { PageToolbar, ModalShell, ConfirmDialog, EmptyState, useNotify } from "components/Convokko";
import { CATEGORIAS, DEPORTES, LETRAS, deporteLabel } from "config";

const equipoVacio = (temporada) => ({
  _id: null,
  nombre: "",
  categoria: "",
  activo: true,
  letra: "",
  temporada,
  competicion: "",
  id_equipo_imd: "",
  deporte: "",
});

const getInitials = (nombre = "") =>
  `${nombre?.[0] || ""}${nombre?.split(" ")[1]?.[0] || ""}`.toUpperCase();

export default function EquiposDashboard() {
  const notify = useNotify();
  const { temporada, setTemporada, temporadas, refrescarTemporadas } = useSeason();

  const [equipos, setEquipos] = useState([]);
  const [jugadores, setJugadores] = useState([]);
  const [entrenadores, setEntrenadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Filtros
  const [searchTerm, setSearchTerm] = useState("");
  const [categoriaFiltro, setCategoriaFiltro] = useState("");
  const [deporteFiltro, setDeporteFiltro] = useState("");
  const [statusFiltro, setStatusFiltro] = useState("");

  // Modales
  const [openEquipoModal, setOpenEquipoModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentEquipo, setCurrentEquipo] = useState(equipoVacio(temporada));
  const [openDetails, setOpenDetails] = useState(false);
  const [selectedEquipo, setSelectedEquipo] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [openTransfer, setOpenTransfer] = useState(false);
  const [selectedJugador, setSelectedJugador] = useState(null);
  const [openAddMember, setOpenAddMember] = useState(false);
  const [equipoForAdd, setEquipoForAdd] = useState(null);
  const [memberSearch, setMemberSearch] = useState("");
  const [memberType, setMemberType] = useState("jugador");
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Migración de temporada
  const [openMigracion, setOpenMigracion] = useState(false);
  const [migracionDestino, setMigracionDestino] = useState(temporada + 1);
  const [resumenOrigen, setResumenOrigen] = useState(null);
  const [migrando, setMigrando] = useState(false);

  const fetchEquipos = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await apiFetch("/api/equipos");
      setEquipos(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPlantillas = useCallback(async () => {
    try {
      const [dataJugadores, dataEntrenadores] = await Promise.all([
        apiFetch("/api/jugadores/ver-jugadores-equipo"),
        apiFetch("/api/entrenadores"),
      ]);
      setJugadores(Array.isArray(dataJugadores) ? dataJugadores : []);
      setEntrenadores(Array.isArray(dataEntrenadores) ? dataEntrenadores : []);
    } catch (err) {
      // Los listados auxiliares no bloquean la pantalla
      console.error("Error cargando plantillas:", err);
    }
  }, []);

  useEffect(() => {
    fetchEquipos();
    fetchPlantillas();
  }, [fetchEquipos, fetchPlantillas]);

  // --- Filtrado coherente: temporada global + filtros locales + búsqueda ---
  const filteredEquipos = useMemo(
    () =>
      equipos.filter((equipo) => {
        if (Number(equipo.temporada) !== Number(temporada)) return false;
        if (categoriaFiltro && equipo.categoria !== categoriaFiltro) return false;
        if (deporteFiltro && equipo.deporte !== deporteFiltro) return false;
        if (statusFiltro === "Activo" && !equipo.activo) return false;
        if (statusFiltro === "Inactivo" && equipo.activo) return false;
        if (searchTerm) {
          const q = searchTerm.toLowerCase();
          const target = `${equipo.nombre || ""} ${equipo.categoria || ""} ${
            equipo.competicion || ""
          } ${equipo.letra || ""}`.toLowerCase();
          if (!target.includes(q)) return false;
        }
        return true;
      }),
    [equipos, temporada, categoriaFiltro, deporteFiltro, statusFiltro, searchTerm]
  );

  // --- CRUD equipo ---
  const handleOpenCreate = () => {
    setEditMode(false);
    setCurrentEquipo(equipoVacio(temporada));
    setOpenEquipoModal(true);
  };

  const handleOpenEdit = (equipo) => {
    setEditMode(true);
    setCurrentEquipo({ ...equipoVacio(equipo.temporada), ...equipo });
    setOpenEquipoModal(true);
  };

  const handleEquipoChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentEquipo((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSaveEquipo = async () => {
    try {
      setSaving(true);
      const payload = {
        nombre: currentEquipo.nombre,
        categoria: currentEquipo.categoria,
        activo: currentEquipo.activo,
        letra: currentEquipo.letra,
        temporada: parseInt(currentEquipo.temporada, 10),
        competicion: currentEquipo.competicion,
        id_equipo_imd: currentEquipo.id_equipo_imd,
        deporte: currentEquipo.deporte,
      };
      if (editMode) {
        await apiFetch("/api/equipos/actualizar-equipo", {
          method: "PUT",
          body: { ...payload, _id: currentEquipo._id },
        });
        notify.success("Equipo actualizado correctamente");
      } else {
        await apiFetch("/api/equipos", { method: "POST", body: payload });
        notify.success("Equipo creado correctamente");
      }
      setOpenEquipoModal(false);
      await fetchEquipos();
      refrescarTemporadas();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEquipo = async () => {
    if (!deleteTarget) return;
    try {
      setSaving(true);
      await apiFetch("/api/equipos/eliminar-equipo", {
        method: "DELETE",
        params: { equipo_id: deleteTarget._id },
      });
      setEquipos((prev) => prev.filter((e) => e._id !== deleteTarget._id));
      notify.success("Equipo eliminado");
      setDeleteTarget(null);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  // --- Detalle ---
  const handleOpenDetails = async (equipo) => {
    setSelectedEquipo(equipo);
    setOpenDetails(true);
    setLoadingDetalle(true);
    try {
      const data = await apiFetch("/api/equipos/jugadores", { params: { equipo_id: equipo._id } });
      setSelectedEquipo((prev) => ({ ...prev, ...data }));
    } catch (err) {
      notify.error(err.message);
    } finally {
      setLoadingDetalle(false);
    }
  };

  // --- Transferencias y altas ---
  const handleTransferJugador = async (nuevoEquipoId) => {
    try {
      setSaving(true);
      await apiFetch("/api/jugadores/cambiar-equipo", {
        method: "PUT",
        params: { jugador_id: selectedJugador._id, equipo_id: nuevoEquipoId },
      });
      notify.success(`${selectedJugador.nombre} transferido`);
      setOpenTransfer(false);
      setSelectedJugador(null);
      await fetchPlantillas();
      if (selectedEquipo) {
        const data = await apiFetch("/api/equipos/jugadores", {
          params: { equipo_id: selectedEquipo._id },
        });
        setSelectedEquipo((prev) => ({ ...prev, ...data }));
      }
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async (memberId) => {
    try {
      setSaving(true);
      const path =
        memberType === "jugador"
          ? "/api/jugadores/cambiar-equipo"
          : "/api/entrenadores/cambiar-equipo";
      const params =
        memberType === "jugador"
          ? { jugador_id: memberId, equipo_id: equipoForAdd._id }
          : { entrenador_id: memberId, equipo_id: equipoForAdd._id };
      await apiFetch(path, { method: "PUT", params });
      notify.success("Miembro añadido al equipo");
      setOpenAddMember(false);
      await fetchPlantillas();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const availableMembers = useMemo(() => {
    const source = memberType === "jugador" ? jugadores : entrenadores;
    return source.filter((m) => {
      if (memberSearch && !m.nombre?.toLowerCase().includes(memberSearch.toLowerCase()))
        return false;
      if (memberType === "jugador") return m.equipo_actual !== equipoForAdd?._id;
      // entrenador: puede llevar varios equipos, solo se excluye si YA está en este
      return !(m.equipos_actuales || []).includes(equipoForAdd?._id);
    });
  }, [memberType, jugadores, entrenadores, memberSearch, equipoForAdd]);

  // --- Migración de temporada ---
  const handleOpenMigracion = async () => {
    setMigracionDestino(Number(temporada) + 1);
    setOpenMigracion(true);
    setResumenOrigen(null);
    try {
      const resumen = await apiFetch("/api/temporadas/resumen", { params: { temporada } });
      setResumenOrigen(resumen);
    } catch (err) {
      notify.error(err.message);
    }
  };

  const handleMigrar = async () => {
    try {
      setMigrando(true);
      const res = await apiFetch("/api/temporadas/migrar", {
        method: "POST",
        body: {
          temporada_origen: Number(temporada),
          temporada_destino: Number(migracionDestino),
          archivar_origen: true,
          mover_plantillas: true,
        },
      });
      notify.success(
        `${res.equipos_creados} equipos creados, ${res.jugadores_movidos} jugadores y ${res.entrenadores_movidos} entrenadores trasladados`
      );
      setOpenMigracion(false);
      await refrescarTemporadas();
      setTemporada(Number(migracionDestino));
      await fetchEquipos();
      await fetchPlantillas();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setMigrando(false);
    }
  };

  const renderSkeleton = () => (
    <Grid container spacing={3}>
      {[...Array(8)].map((_, i) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
          <Card sx={{ p: 3, borderRadius: 3 }}>
            <Stack spacing={2} alignItems="center">
              <Skeleton variant="circular" width={56} height={56} />
              <Skeleton variant="text" width="70%" height={26} />
              <Skeleton variant="rectangular" width="100%" height={28} sx={{ borderRadius: 2 }} />
              <Skeleton variant="rectangular" width="100%" height={36} sx={{ borderRadius: 2 }} />
            </Stack>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3}>
        <PageToolbar
          icon={DiversityIcon}
          title="Equipos"
          count={filteredEquipos.length}
          countLabel={filteredEquipos.length === 1 ? "equipo" : "equipos"}
          action={{
            label: "Nuevo equipo",
            shortLabel: "Nuevo",
            icon: <AddIcon />,
            onClick: handleOpenCreate,
          }}
          search={{
            value: searchTerm,
            onChange: setSearchTerm,
            placeholder: "Buscar por nombre, categoría o competición...",
          }}
          filters={[
            {
              id: "categoria",
              label: "Categoría",
              allLabel: "Todas las categorías",
              value: categoriaFiltro,
              onChange: setCategoriaFiltro,
              options: CATEGORIAS.map((c) => ({ value: c, label: c })),
              minWidth: 120,
            },
            {
              id: "deporte",
              label: "Deporte",
              allLabel: "Todos los deportes",
              value: deporteFiltro,
              onChange: setDeporteFiltro,
              options: DEPORTES,
              minWidth: 110,
            },
            {
              id: "estado",
              label: "Estado",
              allLabel: "Todos los estados",
              value: statusFiltro,
              onChange: setStatusFiltro,
              options: [
                { value: "Activo", label: "Activos" },
                { value: "Inactivo", label: "Inactivos" },
              ],
              minWidth: 100,
            },
          ]}
        >
          <Stack direction="row" justifyContent="flex-end" mt={1.5}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EventRepeatIcon />}
              onClick={handleOpenMigracion}
              sx={{
                borderRadius: 2.5,
                borderColor: "grey.400",
                color: "text.secondary",
                "&:hover": { borderColor: "primary.main", color: "primary.focus" },
              }}
            >
              Migrar temporada
            </Button>
          </Stack>
        </PageToolbar>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {loading ? (
          renderSkeleton()
        ) : filteredEquipos.length === 0 ? (
          <EmptyState
            icon={DiversityIcon}
            title="No hay equipos"
            subtitle={
              equipos.some((e) => Number(e.temporada) === Number(temporada))
                ? "Ningún equipo coincide con los filtros aplicados."
                : `Aún no hay equipos en la temporada ${temporada}. Créalos desde cero o migra los de la temporada anterior.`
            }
            actionLabel="Crear equipo"
            onAction={handleOpenCreate}
          />
        ) : (
          <Grid container spacing={3}>
            {filteredEquipos.map((equipo) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={equipo._id}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "grey.200",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "all .2s ease",
                    opacity: equipo.activo ? 1 : 0.65,
                    "&:hover": {
                      borderColor: "primary.main",
                    },
                  }}
                >
                  <Box p={2.5} flexGrow={1}>
                    <Stack direction="row" spacing={1.75} alignItems="center" mb={1.5}>
                      <Avatar
                        sx={{
                          width: 52,
                          height: 52,
                          fontWeight: 700,
                          fontSize: "1.15rem",
                          bgcolor: "rgba(245,158,11,0.15)",
                          color: "#B45309",
                        }}
                      >
                        {equipo.letra || getInitials(equipo.nombre)}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" fontWeight={700} noWrap lineHeight={1.2}>
                          {equipo.nombre}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {equipo.competicion || "Sin competición"} · {equipo.temporada}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {[equipo.categoria || "Sin categoría", deporteLabel(equipo.deporte)]
                          .filter((v) => v && v !== "—")
                          .join(" · ")}
                      </Typography>
                      {!equipo.activo && <Chip label="Archivado" size="small" variant="outlined" />}
                    </Stack>
                  </Box>

                  <Divider />
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    px={1.5}
                    py={1}
                  >
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleOpenDetails(equipo)}
                      sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                    >
                      Detalle
                    </Button>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Añadir miembro">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEquipoForAdd(equipo);
                            setMemberSearch("");
                            setMemberType("jugador");
                            setOpenAddMember(true);
                          }}
                          sx={{ color: "text.secondary", "&:hover": { color: "warning.main" } }}
                        >
                          <PersonAddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEdit(equipo)}
                          sx={{ color: "text.secondary", "&:hover": { color: "info.main" } }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton
                          size="small"
                          onClick={() => setDeleteTarget(equipo)}
                          sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* FAB solo en móvil */}
      <Fab
        color="primary"
        aria-label="Crear equipo"
        onClick={handleOpenCreate}
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          color: "#fff",
          display: { xs: "flex", sm: "none" },
        }}
      >
        <AddIcon />
      </Fab>

      {/* Crear / editar equipo */}
      <ModalShell
        open={openEquipoModal}
        onClose={() => setOpenEquipoModal(false)}
        title={editMode ? "Editar equipo" : "Nuevo equipo"}
        subtitle={editMode ? currentEquipo.nombre : `Temporada ${currentEquipo.temporada}`}
        icon={DiversityIcon}
        actions={
          <>
            <Button onClick={() => setOpenEquipoModal(false)} sx={{ color: "text.secondary" }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveEquipo}
              startIcon={<SaveIcon />}
              disabled={
                saving ||
                !currentEquipo.nombre ||
                !currentEquipo.categoria ||
                !currentEquipo.deporte
              }
              sx={{ borderRadius: 2 }}
            >
              {saving ? "Guardando..." : editMode ? "Guardar cambios" : "Crear equipo"}
            </Button>
          </>
        }
      >
        <Stack spacing={2.5} mt={0.5}>
          <TextField
            label="Nombre del equipo"
            name="nombre"
            value={currentEquipo.nombre}
            onChange={handleEquipoChange}
            fullWidth
            required
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Categoría</InputLabel>
                <Select
                  name="categoria"
                  value={currentEquipo.categoria}
                  label="Categoría"
                  onChange={handleEquipoChange}
                  sx={{ height: 44 }}
                >
                  {CATEGORIAS.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Letra</InputLabel>
                <Select
                  name="letra"
                  value={currentEquipo.letra || ""}
                  label="Letra"
                  onChange={handleEquipoChange}
                  sx={{ height: 44 }}
                >
                  <MenuItem value="">Sin letra</MenuItem>
                  {LETRAS.map((l) => (
                    <MenuItem key={l} value={l}>
                      {l}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Temporada"
                name="temporada"
                type="number"
                value={currentEquipo.temporada}
                onChange={handleEquipoChange}
                fullWidth
                inputProps={{ min: 2020, max: 2035 }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Deporte</InputLabel>
                <Select
                  name="deporte"
                  value={currentEquipo.deporte}
                  label="Deporte"
                  onChange={handleEquipoChange}
                  sx={{ height: 44 }}
                >
                  {DEPORTES.map((d) => (
                    <MenuItem key={d.value} value={d.value}>
                      {d.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <TextField
            label="Competición"
            name="competicion"
            value={currentEquipo.competicion || ""}
            onChange={handleEquipoChange}
            fullWidth
            placeholder="Ej: IMD"
          />
          <TextField
            label="ID equipo IMD"
            name="id_equipo_imd"
            value={currentEquipo.id_equipo_imd || ""}
            onChange={handleEquipoChange}
            fullWidth
            placeholder="Identificador en la web del IMD (opcional)"
          />
          <FormControlLabel
            control={
              <Checkbox
                name="activo"
                checked={Boolean(currentEquipo.activo)}
                onChange={handleEquipoChange}
              />
            }
            label="Equipo activo"
          />
        </Stack>
      </ModalShell>

      {/* Detalle de equipo */}
      <ModalShell
        open={openDetails}
        onClose={() => {
          setOpenDetails(false);
          setSelectedEquipo(null);
        }}
        title={selectedEquipo?.nombre || ""}
        subtitle={`${selectedEquipo?.categoria || ""} · Temporada ${
          selectedEquipo?.temporada || ""
        }`}
        icon={GroupsIcon}
        maxWidth="md"
      >
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <Paper
              elevation={0}
              sx={{ p: 2.5, borderRadius: 3, bgcolor: "grey.100", height: "100%" }}
            >
              <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                Información
              </Typography>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <SchoolIcon color="primary" fontSize="small" />
                  <Typography variant="body2">
                    {selectedEquipo?.categoria || "Sin categoría"}
                    {selectedEquipo?.letra ? ` · Letra ${selectedEquipo.letra}` : ""}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <CalendarIcon color="primary" fontSize="small" />
                  <Typography variant="body2">Temporada {selectedEquipo?.temporada}</Typography>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <TrophyIcon color="primary" fontSize="small" />
                  <Typography variant="body2">
                    {selectedEquipo?.competicion || "Sin competición"}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <SportsIcon color="primary" fontSize="small" />
                  <Typography variant="body2">{deporteLabel(selectedEquipo?.deporte)}</Typography>
                </Stack>
                <Box>
                  <Chip
                    label={selectedEquipo?.activo ? "Activo" : "Archivado"}
                    color={selectedEquipo?.activo ? "success" : "default"}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper
              elevation={0}
              sx={{ p: 2.5, borderRadius: 3, bgcolor: "grey.100", height: "100%" }}
            >
              <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                Plantilla
              </Typography>
              <Stack direction="row" spacing={2}>
                <Box textAlign="center" flex={1}>
                  <Typography variant="h4" fontWeight={800} color="primary.focus">
                    {selectedEquipo?.jugadores?.length ?? "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Jugadores
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box textAlign="center" flex={1}>
                  <Typography variant="h4" fontWeight={800} color="primary.focus">
                    {selectedEquipo?.entrenadores?.length ?? "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Entrenadores
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>
              Jugadores
            </Typography>
            {loadingDetalle ? (
              <Stack spacing={1.5}>
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} variant="rounded" height={48} sx={{ borderRadius: 2 }} />
                ))}
              </Stack>
            ) : selectedEquipo?.jugadores?.length ? (
              <List dense disablePadding>
                {selectedEquipo.jugadores.map((j) => (
                  <ListItem
                    key={j._id}
                    sx={{
                      px: 1.5,
                      borderRadius: 2,
                      mb: 0.5,
                      "&:hover": { bgcolor: "grey.100" },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{ width: 34, height: 34, fontSize: "0.85rem", bgcolor: "info.main" }}
                      >
                        {getInitials(j.nombre)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={j.nombre}
                      secondary={j.posicion || "Sin posición"}
                      primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 600 }}
                      secondaryTypographyProps={{ fontSize: "0.75rem" }}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Transferir a otro equipo">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedJugador(j);
                            setOpenTransfer(true);
                          }}
                          sx={{ color: "text.secondary", "&:hover": { color: "info.main" } }}
                        >
                          <TransferIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" py={2}>
                No hay jugadores en este equipo.
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>
              Entrenadores
            </Typography>
            {loadingDetalle ? (
              <Skeleton variant="rounded" height={48} sx={{ borderRadius: 2 }} />
            ) : selectedEquipo?.entrenadores?.length ? (
              <List dense disablePadding>
                {selectedEquipo.entrenadores.map((e) => (
                  <ListItem key={e._id} sx={{ px: 1.5, borderRadius: 2, mb: 0.5 }}>
                    <ListItemAvatar>
                      <Avatar
                        sx={{ width: 34, height: 34, fontSize: "0.85rem", bgcolor: "dark.main" }}
                      >
                        {getInitials(e.nombre)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={e.nombre}
                      primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 600 }}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" py={2}>
                No hay entrenadores asignados.
              </Typography>
            )}
          </Grid>
        </Grid>
      </ModalShell>

      {/* Transferir jugador */}
      <ModalShell
        open={openTransfer}
        onClose={() => setOpenTransfer(false)}
        title="Transferir jugador"
        subtitle={selectedJugador?.nombre}
        icon={TransferIcon}
        color="info"
      >
        <Typography variant="body2" color="text.secondary" mb={2}>
          Selecciona el equipo de destino (temporada {temporada}):
        </Typography>
        <List disablePadding>
          {equipos
            .filter(
              (e) =>
                e._id !== selectedJugador?.equipo_actual &&
                Number(e.temporada) === Number(temporada)
            )
            .map((equipo) => (
              <ListItem
                key={equipo._id}
                button
                onClick={() => handleTransferJugador(equipo._id)}
                disabled={saving}
                sx={{
                  border: "1px solid",
                  borderColor: "grey.200",
                  borderRadius: 3,
                  mb: 1,
                  "&:hover": { borderColor: "info.main", bgcolor: "rgba(26,115,232,.04)" },
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: "info.main", fontWeight: 700 }}>
                    {equipo.letra || getInitials(equipo.nombre)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={equipo.nombre}
                  secondary={`${equipo.categoria || ""} · ${equipo.temporada}`}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
            ))}
        </List>
      </ModalShell>

      {/* Añadir miembro */}
      <ModalShell
        open={openAddMember}
        onClose={() => setOpenAddMember(false)}
        title={`Añadir a ${equipoForAdd?.nombre || ""}`}
        icon={PersonAddIcon}
        color="dark"
      >
        <Stack spacing={2}>
          <FormControl fullWidth>
            <InputLabel>Tipo de miembro</InputLabel>
            <Select
              value={memberType}
              label="Tipo de miembro"
              onChange={(e) => setMemberType(e.target.value)}
              sx={{ height: 44 }}
            >
              <MenuItem value="jugador">Jugador</MenuItem>
              <MenuItem value="entrenador">Entrenador</MenuItem>
            </Select>
          </FormControl>
          <TextField
            placeholder={`Buscar ${memberType}...`}
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ maxHeight: 380, overflowY: "auto" }}>
            {availableMembers.length ? (
              <List disablePadding>
                {availableMembers.map((m) => (
                  <ListItem
                    key={m._id}
                    button
                    disabled={saving}
                    onClick={() => handleAddMember(m._id)}
                    sx={{
                      border: "1px solid",
                      borderColor: "grey.200",
                      borderRadius: 3,
                      mb: 1,
                      "&:hover": { borderColor: "primary.main" },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: memberType === "jugador" ? "info.main" : "dark.main",
                          fontWeight: 700,
                        }}
                      >
                        {getInitials(m.nombre)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={m.nombre}
                      secondary={
                        memberType === "jugador"
                          ? m.nombre_equipo_actual || m.posicion || "Sin equipo"
                          : m.nombre_equipo_actual || "Sin equipo"
                      }
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Stack alignItems="center" py={4} spacing={1}>
                <Person sx={{ fontSize: 44, color: "grey.400" }} />
                <Typography variant="body2" color="text.secondary">
                  No hay {memberType === "jugador" ? "jugadores" : "entrenadores"} disponibles
                </Typography>
              </Stack>
            )}
          </Box>
        </Stack>
      </ModalShell>

      {/* Migración de temporada */}
      <ModalShell
        open={openMigracion}
        onClose={() => !migrando && setOpenMigracion(false)}
        title="Migrar temporada"
        subtitle={`De ${temporada} a ${migracionDestino}`}
        icon={EventRepeatIcon}
        color="dark"
        actions={
          <>
            <Button
              onClick={() => setOpenMigracion(false)}
              disabled={migrando}
              sx={{ color: "text.secondary" }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleMigrar}
              disabled={migrando || Number(migracionDestino) <= Number(temporada)}
              startIcon={<EventRepeatIcon />}
              sx={{ borderRadius: 2 }}
            >
              {migrando ? "Migrando..." : "Iniciar migración"}
            </Button>
          </>
        }
      >
        <Stack spacing={2.5}>
          <Alert severity="info" sx={{ borderRadius: 3 }}>
            Se crearán copias de todos los equipos <b>activos</b> de la temporada {temporada} en la
            nueva temporada, se trasladarán jugadores y entrenadores (guardando su historial) y los
            equipos antiguos quedarán <b>archivados</b> con todos sus partidos y entrenamientos
            intactos.
          </Alert>

          {resumenOrigen ? (
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: "grey.100" }}>
              <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                Temporada {temporada} (origen)
              </Typography>
              <Grid container spacing={1.5}>
                {[
                  ["Equipos activos", resumenOrigen.equipos_activos],
                  ["Jugadores", resumenOrigen.jugadores],
                  ["Entrenadores", resumenOrigen.entrenadores],
                  ["Partidos", resumenOrigen.partidos],
                ].map(([label, valor]) => (
                  <Grid item xs={6} sm={3} key={label}>
                    <Box textAlign="center">
                      <Typography variant="h5" fontWeight={800} color="primary.focus">
                        {valor}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          ) : (
            <Skeleton variant="rounded" height={90} sx={{ borderRadius: 3 }} />
          )}

          <TextField
            label="Temporada de destino"
            type="number"
            value={migracionDestino}
            onChange={(e) => setMigracionDestino(e.target.value)}
            fullWidth
            inputProps={{ min: Number(temporada) + 1, max: 2040 }}
            helperText="Normalmente el año siguiente a la temporada actual"
          />
        </Stack>
      </ModalShell>

      {/* Confirmación de borrado */}
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Eliminar equipo"
        message={
          <>
            ¿Seguro que quieres eliminar <b>{deleteTarget?.nombre}</b>? Esta acción no se puede
            deshacer. Si el equipo tiene partidos o jugadores asociados, el sistema lo impedirá.
          </>
        }
        onConfirm={handleDeleteEquipo}
        onClose={() => setDeleteTarget(null)}
        loading={saving}
      />

      <Footer />
    </DashboardLayout>
  );
}
